import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Clock, DollarSign, GitBranch, BarChart3, FileText, Layout } from 'lucide-react';

const AdvancedAnalyticsHub: React.FC = () => {
    const tools = [
        {
            name: 'Weibull Analysis',
            icon: TrendingUp,
            description: 'B10/B50 life prediction & reliability modeling',
            color: 'bg-blue-500',
            link: '/analytics/weibull',
            features: ['Life predictions (B10, B50, B90)', 'Confidence intervals', 'Shape & scale parameters', 'Goodness of fit (R²)'],
            status: 'available',
        },
        {
            name: 'MTBF/MTTR Calculator',
            icon: Clock,
            description: 'Mean Time Between Failures & repair time analysis',
            color: 'bg-green-500',
            link: '/analytics/mtbf',
            features: ['MTBF calculation', 'Availability analysis', 'Failure distribution', 'Cost metrics'],
            status: 'available',
        },
        {
            name: 'Warranty Tracking',
            icon: DollarSign,
            description: 'Track claims and forecast future warranty costs',
            color: 'bg-yellow-500',
            link: '/analytics/warranty',
            features: ['Claim management', 'Cost forecasting', 'Trend analysis', '6-month predictions'],
            status: 'available',
        },
        {
            name: 'Fault Tree Analysis',
            icon: GitBranch,
            description: 'Build and analyze fault trees for system reliability',
            color: 'bg-purple-500',
            link: '/analytics/fta',
            features: ['Visual tree editor', 'AND/OR/NOT gates', 'Minimal cut sets', 'Importance measures'],
            status: 'available',
        },
        {
            name: 'Reliability Block Diagrams (RBD)',
            icon: Layout,
            description: 'Model system availability and redundancy',
            color: 'bg-indigo-500',
            link: '/analytics/rbd',
            features: ['Series/Parallel config', 'K-out-of-N voting', 'System R calculation', 'Availability modeling'],
            status: 'available',
        },
        {
            name: 'Advanced Reports',
            icon: FileText,
            description: 'Generate comprehensive reliability reports',
            color: 'bg-emerald-500',
            link: '/reports/advanced',
            features: ['Custom templates', 'PDF/Excel export', 'Executive summaries', 'Multi-source data'],
            status: 'available',
        },
        {
            name: 'Pareto Analysis',
            icon: BarChart3,
            description: '80/20 analysis for failure modes',
            color: 'bg-orange-500',
            link: '/analytics',
            features: ['Failure prioritization', 'Cost analysis', 'Visual charts', 'Top contributors'],
            status: 'available',
        },
    ];

    return (
        <div className="p-8 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Advanced Analytics</h1>
                <p className="text-gray-600">
                    Powerful reliability engineering tools for Weibull, MTBF/MTTR, warranty forecasting, and fault tree analysis
                </p>
            </div>

            {/* Tool Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
                {tools.map((tool, index) => {
                    const Icon = tool.icon;
                    const isAvailable = tool.status === 'available';

                    return (
                        <Link
                            key={index}
                            to={isAvailable ? tool.link : '#'}
                            className={`block p-6 bg-white rounded-xl border-2 border-gray-200 transition-all ${isAvailable
                                ? 'hover:border-blue-400 hover:shadow-lg cursor-pointer'
                                : 'opacity-60 cursor-not-allowed'
                                }`}
                        >
                            <div className="flex items-start gap-4">
                                <div className={`p-3 ${tool.color} rounded-lg flex-shrink-0`}>
                                    <Icon className="w-6 h-6 text-white" />
                                </div>

                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="text-xl font-semibold text-gray-900">{tool.name}</h3>
                                        {!isAvailable && (
                                            <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
                                                Coming Soon
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-gray-600 mb-4">{tool.description}</p>

                                    <div className="space-y-1">
                                        {tool.features.map((feature, idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                                <span>{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </div>

            {/* Information Panel */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Why Use Advanced Analytics?</h2>

                <div className="grid md:grid-cols-3 gap-6">
                    <div>
                        <h3 className="font-semibold text-blue-900 mb-2">Predict Failures</h3>
                        <p className="text-sm text-gray-700">
                            Use Weibull analysis to predict when failures will occur and plan preventive maintenance
                            at the optimal time to minimize downtime and costs.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-semibold text-blue-900 mb-2">Optimize Reliability</h3>
                        <p className="text-sm text-gray-700">
                            FTA helps identify critical failure paths and optimize system design for maximum reliability.
                            Target improvements where they matter most.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-semibold text-blue-900 mb-2">Control Costs</h3>
                        <p className="text-sm text-gray-700">
                            Warranty forecasting predicts future costs with 90%+ confidence, enabling better budgeting
                            and financial planning for reliability programs.
                        </p>
                    </div>
                </div>
            </div>

            {/* Best Practices */}
            <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Best Practices</h2>

                <div className="space-y-4">
                    <div className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">
                            1
                        </div>
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-1">Start with Weibull Analysis</h4>
                            <p className="text-sm text-gray-600">
                                Collect failure time data from testing or field returns. At least 5-10 data points needed
                                for reliable parameters. Use B10 life for maintenance scheduling.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-semibold">
                            2
                        </div>
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-1">Track MTBF/MTTR Continuously</h4>
                            <p className="text-sm text-gray-600">
                                Log every failure event with downtime and repair time. Calculate fleet-level metrics monthly.
                                Target {'>'} 99% availability for critical systems.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center font-semibold">
                            3
                        </div>
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-1">Build FTA for Critical Systems</h4>
                            <p className="text-sm text-gray-600">
                                Create fault trees for high-risk failure scenarios. Use FMEA data to populate basic events.
                                Update trees after major design changes or field failures.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-semibold">
                            4
                        </div>
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-1">Forecast Warranty Costs Quarterly</h4>
                            <p className="text-sm text-gray-600">
                                Update forecasts every quarter with latest claim data. Use 95% confidence intervals for budgeting.
                                Monitor trends to detect quality issues early.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdvancedAnalyticsHub;
